import React from 'react';
import { HelpCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Card } from '../components/ui/card';

const FAQ = () => {
  const { language } = useLanguage();

  const faqs = {
    general: [
      {
        question: { fr: 'Quels sont vos domaines d\'intervention ?', en: 'What are your areas of intervention?' },
        answer: { fr: 'Nous intervenons dans plusieurs domaines : équipements professionnels, informatique & électronique, électroménager professionnel, maintenance & réparations, véhicules & logistique, énergie & éclairage, et capacités aériennes.', en: 'We operate in several areas: professional equipment, IT & electronics, professional appliances, maintenance & repairs, vehicles & logistics, energy & lighting, and air capabilities.' }
      },
      {
        question: { fr: 'Dans quelles régions êtes-vous présents ?', en: 'In which regions are you present?' },
        answer: { fr: 'Notre siège social est à Dakar, Sénégal. Nous intervenons dans tout le Sénégal et prévoyons de nous étendre dans d\'autres pays africains : Maroc, Mauritanie, Guinée, Côte d\'Ivoire, Djibouti et Congo.', en: 'Our headquarters are in Dakar, Senegal. We operate throughout Senegal and plan to expand to other African countries: Morocco, Mauritania, Guinea, Ivory Coast, Djibouti, and Congo.' }
      },
      {
        question: { fr: 'Comment puis-je obtenir un devis ?', en: 'How can I get a quote?' },
        answer: { fr: 'Vous pouvez nous contacter via notre formulaire en ligne sur la page Contact, par téléphone au +221 77 266 93 93, ou par email à info@prestacosn.com. Notre équipe vous répondra dans les 24 heures.', en: 'You can contact us via our online form on the Contact page, by phone at +221 77 266 93 93, or by email at info@prestacosn.com. Our team will respond within 24 hours.' }
      }
    ],
    services: [
      {
        question: { fr: 'Proposez-vous des contrats de maintenance ?', en: 'Do you offer maintenance contracts?' },
        answer: { fr: 'Oui, nous proposons des contrats de maintenance préventive et corrective adaptés à vos besoins. Nos contrats incluent des inspections régulières, des interventions prioritaires et un support technique 24/7.', en: 'Yes, we offer preventive and corrective maintenance contracts tailored to your needs. Our contracts include regular inspections, priority interventions, and 24/7 technical support.' }
      },
      {
        question: { fr: 'Quels types de véhicules proposez-vous ?', en: 'What types of vehicles do you offer?' },
        answer: { fr: 'Nous proposons une large gamme de véhicules : voitures de tourisme, camions, bus, véhicules de chantier. Nous offrons des options de vente et de location longue durée avec gestion de flotte complète.', en: 'We offer a wide range of vehicles: passenger cars, trucks, buses, construction vehicles. We offer sales and long-term rental options with complete fleet management.' }
      },
      {
        question: { fr: 'Installez-vous des panneaux solaires ?', en: 'Do you install solar panels?' },
        answer: { fr: 'Oui, nous sommes spécialisés dans l\'installation de solutions énergétiques durables incluant panneaux solaires, éclairage LED, groupes électrogènes et batteries de secours. Nous proposons des solutions complètes avec maintenance garantie.', en: 'Yes, we specialize in installing sustainable energy solutions including solar panels, LED lighting, generators, and backup batteries. We offer complete solutions with guaranteed maintenance.' }
      }
    ],
    aircraft: [
      {
        question: { fr: 'Quels types d\'aéronefs proposez-vous ?', en: 'What types of aircraft do you offer?' },
        answer: { fr: 'Nous proposons une large gamme d\'aéronefs : avions de transport léger et moyen, avions d\'entraînement, hélicoptères légers et moyens, avions de patrouille maritime, drones MALE, et aéronefs VIP/EVASAN.', en: 'We offer a wide range of aircraft: light and medium transport aircraft, training aircraft, light and medium helicopters, maritime patrol aircraft, MALE drones, and VIP/MEDEVAC aircraft.' }
      },
      {
        question: { fr: 'Proposez-vous des formations pour les pilotes ?', en: 'Do you offer training for pilots?' },
        answer: { fr: 'Oui, nous proposons des formations complètes pour pilotes incluant conversion type, procédures opérationnelles, et tactiques. Nous organisons également des formations pour les techniciens de maintenance.', en: 'Yes, we offer comprehensive pilot training including type conversion, operational procedures, and tactics. We also organize training for maintenance technicians.' }
      },
      {
        question: { fr: 'Assurez-vous le soutien logistique ?', en: 'Do you provide logistics support?' },
        answer: { fr: 'Oui, nous assurons un soutien logistique complet incluant fourniture de pièces détachées, maintenance préventive, support technique 24/7, et transfert de compétences pour une autonomie à moyen terme.', en: 'Yes, we provide complete logistics support including spare parts supply, preventive maintenance, 24/7 technical support, and skills transfer for medium-term autonomy.' }
      }
    ],
    payment: [
      {
        question: { fr: 'Quels sont vos modes de paiement ?', en: 'What are your payment methods?' },
        answer: { fr: 'Nous acceptons les virements bancaires, les chèques d\'entreprise et les paiements échelonnés selon les projets. Pour les grands projets, nous proposons des facilités de paiement adaptées.', en: 'We accept bank transfers, company checks, and installment payments depending on projects. For large projects, we offer adapted payment facilities.' }
      },
      {
        question: { fr: 'Proposez-vous des facilités de financement ?', en: 'Do you offer financing facilities?' },
        answer: { fr: 'Oui, nous travaillons avec plusieurs partenaires financiers pour proposer des solutions de financement adaptées à vos besoins, notamment pour les équipements et véhicules.', en: 'Yes, we work with several financial partners to offer financing solutions tailored to your needs, particularly for equipment and vehicles.' }
      }
    ]
  };

  const categories = [
    { key: 'general', title: { fr: 'Questions Générales', en: 'General Questions' } },
    { key: 'services', title: { fr: 'Services & Produits', en: 'Services & Products' } },
    { key: 'aircraft', title: { fr: 'Capacités Aériennes', en: 'Air Capabilities' } },
    { key: 'payment', title: { fr: 'Paiement & Financement', en: 'Payment & Financing' } }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1606857521015-7f9fcf423740)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-blue-900/85"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <HelpCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Foire Aux Questions' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Trouvez rapidement les réponses à vos questions'
              : 'Quickly find answers to your questions'}
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-gradient-to-br from-white via-blue-50/30 to-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {categories.map((category) => (
              <div key={category.key}>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {category.title[language]}
                </h2>
                <Card className="p-6 bg-white shadow-lg">
                  <Accordion type="single" collapsible className="space-y-4">
                    {faqs[category.key].map((faq, index) => (
                      <AccordionItem
                        key={index}
                        value={`${category.key}-${index}`}
                        className="border-2 border-gray-200 rounded-lg px-6 hover:border-blue-500 transition-colors"
                      >
                        <AccordionTrigger className="text-left hover:no-underline">
                          <span className="font-semibold text-gray-900">
                            {faq.question[language]}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-gray-600 pt-4">
                          {faq.answer[language]}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <Card className="max-w-3xl mx-auto p-12 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">
              {language === 'fr' ? 'Vous ne trouvez pas votre réponse ?' : 'Can\'t find your answer?'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr'
                ? 'Notre équipe est là pour vous aider'
                : 'Our team is here to help you'}
            </p>
            <a href="/contact">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg">
                {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
              </button>
            </a>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
